// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@stanion/App/Components/Notify";
import { xLabeledPDPTextStyles } from "@stanion/App/SharedStyle/xLabeledTextStyle";
import { xScreen } from "@stanion/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsPropWarningStyles {
    wrapper?: InjectableCss;
    labelText?: TypographyProps;
    linkStyle?: LinkPresentationProps;
    modal?: ModalPresentationProps;
}

export const productDetailsPropWarningStyles: ProductDetailsPropWarningStyles = {
    wrapper: xLabeledPDPTextStyles.wrapper,
    labelText: xLabeledPDPTextStyles.label,
    linkStyle: {
        typographyProps: {
            transform: "uppercase",
        },
    },
    modal: {
        sizeVariant: "small",
    },
};

const styles = productDetailsPropWarningStyles;

const ProductDetailsPropWarning: React.FC<Props> = ({ product }) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const isHazardousGood = product?.detail?.isHazardousGood;
    if (!isHazardousGood) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.labelText}>{translate("Prop 65")}</Typography>
                <Link onClick={() => setModalIsOpen(true)} data-test-selector="Prop65Warning" {...styles.linkStyle}>
                    {translate("warning")}
                </Link>
            </StyledWrapper>
            <Modal {...styles.modal} isOpen={modalIsOpen} handleClose={() => setModalIsOpen(false)}>
                <Notify
                    type="warning"
                    css={css`
                        max-width: 100%;
                        ${xScreen("lte", "xs")} {
                            max-width: 100%;
                        }
                    `}
                >
                    {siteMessage("Custom_Prop65Warning_message")}
                </Notify>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPropWarning),
    definition: {
        displayName: "Prop 65 Warning",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
